import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  TextField,
} from "@mui/material";
import "./FilterPanel.css";
import request from "../../api/request";
import { BackendAPI } from "../../const/api";

export interface QueryOptionsItem {
  label: string;
  value: string;
  data?: any;
}

export interface SimpleMaterial {
  id: string;
  name: string;
  model: string;
}

export interface FilterOption {
  key: string;
  label: string;
  type: "text" | "select" | "multiselect";
  options?: (QueryOptionsItem | string)[];
  defaultValue?: (QueryOptionsItem | string)[];
}

export const getSimpleMaterials = async (): Promise<QueryOptionsItem[]> => {
  const res = await request.get(BackendAPI.materialAll);
  if (!res.success) {
    return [];
  }
  const materials: any[] = res.data || [];
  return materials
    .filter((m) => !m?.disabled)
    .map(
    (m) =>
      ({
        value: m.id,
        label: `${m.name} - ${m.model}`,
        data: m,
      }) as QueryOptionsItem,
  );
};

interface FilterPanelProps {
  filterOptions: FilterOption[];
  onFilterChange: (filters: any) => void;
  onExport?: (filters: any) => void;
  needDate?: boolean;
}

const FilterPanel = ({
  filterOptions,
  onFilterChange,
  onExport = undefined,
  needDate = true,
}: FilterPanelProps) => {
  const defaultFilters = {};
  filterOptions
    .filter((o) => o.defaultValue)
    .forEach((o) => {
      defaultFilters[o.key] = o.defaultValue;
    });
  const [filters, setFilters] = useState(defaultFilters);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const defaultsSyncedRef = useRef(false);

  // defaultValue 只影响展示；挂载时同步到父组件，保证首屏请求带默认筛选
  useEffect(() => {
    const defaults: Record<string, unknown> = {};
    filterOptions
      .filter((o) => o.defaultValue && o.defaultValue.length > 0)
      .forEach((o) => {
        defaults[o.key] = o.defaultValue;
      });
    if (Object.keys(defaults).length === 0) {
      return;
    }
    setFilters((prev) => ({ ...defaults, ...prev }));
    if (!defaultsSyncedRef.current) {
      defaultsSyncedRef.current = true;
      onFilterChange({ ...defaults, start_date: startDate, end_date: endDate });
    }
  }, [filterOptions, onFilterChange, startDate, endDate]);

  const handleApplyFilters = () => {
    onFilterChange({ ...filters, start_date: startDate, end_date: endDate });
  };

  const handleOnExport = () => {
    onExport &&
      onExport({ ...filters, start_date: startDate, end_date: endDate });
  };

  const renderFilterFields = useMemo(() => {
    const handleFilterChange = (key, value) => {
      if (value === "") {
        // delete key from filter
        const newFilters = { ...filters };
        delete newFilters[key];
        setFilters(newFilters);
      } else {
        setFilters({ ...filters, [key]: value });
      }
    };

    return filterOptions.map((option: FilterOption) => {
      if (option.type === "text") {
        return (
          <TextField
            key={option.key}
            className="filter-field"
            margin="normal"
            fullWidth
            label={option.label}
            value={filters[option.key] || ""}
            onChange={(e) => handleFilterChange(option.key, e.target.value)}
          />
        );
      } else if (option.type === "select") {
        return (
          <FormControl
            fullWidth
            margin="normal"
            key={option.key}
            className="filter-field"
          >
            <Autocomplete
              id={`sales-label`}
              options={option.options || []}
              onChange={(e, value) => {
                // 如果value是对象，提取其value属性；如果是字符串，直接使用
                const filterValue = typeof value === 'object' && value !== null ? value.value : value;
                handleFilterChange(option.key, filterValue);
              }}
              renderInput={(params) => (
                <TextField {...params} label={option.label} />
              )}
              defaultValue={option.defaultValue ? option.defaultValue[0] : null}
            />
          </FormControl>
        );
      } else if (option.type === "multiselect") {
        return (
          <FormControl
            fullWidth
            margin="normal"
            key={option.key}
            className="filter-field"
          >
            <Autocomplete
              multiple
              id={`${option.key}-label`}
              limitTags={1}
              options={option.options || []}
              defaultValue={option.defaultValue}
              onChange={(e, value) =>
                handleFilterChange(
                  option.key,
                  value.map((item) => item),
                )
              }
              renderInput={(params) => (
                <TextField {...params} label={`${option.label}`} />
              )}
            />
          </FormControl>
        );
      }
      return null;
    });
  }, [filterOptions, filters]);

  return (
    <Box component="form" noValidate className="filter-panel">
      {renderFilterFields}
      {needDate && (
        <div className="date-field-container">
          <TextField
            label="开始时间"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="结束时间"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </div>
      )}
      <Button type="button" variant="contained" onClick={handleApplyFilters}>
        搜索
      </Button>
      {onExport && (
        <Button type="button" variant="contained" onClick={handleOnExport}>
          按搜索条件导出
        </Button>
      )}
    </Box>
  );
};

export default FilterPanel;
